import type { Worker, WorkType } from "@eckersdorf/api"

interface PrintScheduleData {
  workplaceName: string
  month: string
  workers: readonly Worker[]
  schedules: Record<string, Record<string, Record<string, WorkType | null>>>
}

// Polish day names (short)
const DAY_NAMES: Record<number, string> = {
  0: "Nd",
  1: "Pn",
  2: "Wt",
  3: "Śr",
  4: "Cz",
  5: "Pt",
  6: "So",
}

function getDaysInMonth(month: string): Array<{
  dateStr: string
  dayOfMonth: number
  dayOfWeek: number
  isWeekend: boolean
  isSunday: boolean
}> {
  const parts = month.split("-")
  const year = parseInt(parts[0] || "2024", 10)
  const monthNum = parseInt(parts[1] || "1", 10)
  const daysInMonth = new Date(year, monthNum, 0).getDate()

  const days: Array<{
    dateStr: string
    dayOfMonth: number
    dayOfWeek: number
    isWeekend: boolean
    isSunday: boolean
  }> = []

  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, monthNum - 1, d)
    const dayOfWeek = date.getDay()
    const dateStr = `${year}-${String(monthNum).padStart(2, "0")}-${String(d).padStart(2, "0")}`
    
    days.push({
      dateStr,
      dayOfMonth: d,
      dayOfWeek,
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
      isSunday: dayOfWeek === 0
    })
  }
  return days
}

function getCellContent(
  workerId: string,
  dateStr: string,
  schedules: Record<string, Record<string, Record<string, WorkType | null>>>
): string {
  const workerSchedule = schedules[workerId]
  if (!workerSchedule) return ""
  const daySchedule = workerSchedule[dateStr]
  if (!daySchedule) return ""

  const types = new Set<WorkType>()
  let minHour = 24
  let maxHour = -1
  let workType: "seller" | "butcher" | null = null

  const hours = Object.keys(daySchedule).sort()
  for (const hStr of hours) {
    const type = daySchedule[hStr]
    if (type) {
      types.add(type)
      const h = parseInt(hStr, 10)
      if (h < minHour) minHour = h
      if (h > maxHour) maxHour = h
      if (type === "seller" || type === "butcher") {
        workType = type
      }
    }
  }

  if (types.size === 0) return ""
  if (types.has("holiday")) return "<b>Św</b>"
  if (types.has("vacation")) return "<b>U</b>"
  if (types.has("sickLeave")) return "<b>Ch</b>"
  if (types.has("wolne")) return "<b>W</b>"

  if (minHour <= maxHour && workType) {
    const label = workType === "seller" ? "S" : "L"
    return `<div class="shift-cell"><span class="hours">${minHour}-${maxHour + 1}</span><span class="type">${label}</span></div>`
  }
  return ""
}

function getShortName(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}.${lastName}`
}

function generatePrintHtml(data: PrintScheduleData): string {
  const days = getDaysInMonth(data.month)
  
  // Generate worker header cells (vertical text)
  let workerHeaders = ""
  for (const worker of data.workers) {
    const name = getShortName(worker.firstName, worker.lastName)
    workerHeaders += `<th class="worker-header"><div class="vertical-text">${name}</div></th>`
  }

  // Generate rows (one per day)
  let rows = ""
  for (const day of days) {
    const rowClass = day.isSunday ? "sunday-row" : day.isWeekend ? "weekend-row" : ""
    const dayName = DAY_NAMES[day.dayOfWeek] || ""
    
    let cells = ""
    for (const worker of data.workers) {
      const content = getCellContent(worker.id, day.dateStr, data.schedules)
      const cellClass = day.isSunday ? "sunday-cell" : day.isWeekend ? "weekend-cell" : ""
      cells += `<td class="${cellClass}">${content}</td>`
    }
    
    const dateCell = `<td class="date-cell ${rowClass}">${String(day.dayOfMonth).padStart(2, "0")} ${dayName}</td>`
    rows += `<tr>${dateCell}${cells}</tr>`
  }

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Harmonogram - ${data.workplaceName}</title>
  <style>
    @page {
      size: portrait;
      margin: 6mm;
    }
    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: Arial, sans-serif;
      font-size: 10px;
      color: #000;
      background: #fff;
    }
    .header {
      border-bottom: 2px solid #000;
      padding-bottom: 6px;
      margin-bottom: 8px;
    }
    .header h1 {
      font-size: 16px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .header h2 {
      font-size: 12px;
      font-weight: normal;
      color: #444;
      margin-top: 2px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
    }
    th, td {
      border: 1px solid #000;
      padding: 2px;
      text-align: center;
      vertical-align: middle;
    }
    th {
      background: #f0f0f0;
      font-size: 9px;
    }
    .date-cell {
      width: 45px;
      min-width: 45px;
      text-align: left;
      padding-left: 4px;
      font-weight: bold;
      font-size: 9px;
    }
    .worker-header {
      min-width: 24px;
      padding: 2px;
      height: 70px;
    }
    .vertical-text {
      writing-mode: vertical-rl;
      text-orientation: mixed;
      transform: rotate(180deg);
      white-space: nowrap;
      font-size: 9px;
      font-weight: bold;
    }
    td {
      height: 18px;
      font-size: 9px;
    }
    .weekend-cell, .weekend-row {
      background: #f5f5f5;
    }
    .sunday-cell, .sunday-row {
      background: #fee2e2;
    }
    .shift-cell {
      display: flex;
      flex-direction: column;
      align-items: center;
      line-height: 1.1;
    }
    .shift-cell .hours {
      font-size: 9px;
      font-weight: 500;
    }
    .shift-cell .type {
      font-size: 7px;
      text-transform: uppercase;
    }
    .legend {
      margin-top: 10px;
      padding-top: 6px;
      border-top: 1px solid #000;
      font-size: 9px;
    }
    .legend h3 {
      font-size: 9px;
      text-transform: uppercase;
      margin-bottom: 4px;
    }
    .legend-items {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>${data.workplaceName}</h1>
    <h2>Harmonogram pracy: ${data.month}</h2>
  </div>
  
  <table>
    <thead>
      <tr>
        <th class="date-cell">Data</th>
        ${workerHeaders}
      </tr>
    </thead>
    <tbody>
      ${rows}
    </tbody>
  </table>
  
  <div class="legend">
    <h3>Legenda</h3>
    <div class="legend-items">
      <div><b>6-14 S</b> - Sklep</div>
      <div><b>6-14 L</b> - Lada</div>
      <div><b>U</b> - Urlop</div>
      <div><b>Ch</b> - Chorobowe</div>
      <div><b>Św</b> - Święto</div>
      <div><b>W</b> - Wolne</div>
    </div>
  </div>
</body>
</html>
  `
}

export function printSchedule(data: PrintScheduleData): void {
  const html = generatePrintHtml(data)
  
  // Create hidden iframe
  const iframe = document.createElement("iframe")
  iframe.style.position = "absolute"
  iframe.style.top = "-10000px"
  iframe.style.left = "-10000px"
  iframe.style.width = "0"
  iframe.style.height = "0"
  document.body.appendChild(iframe)
  
  const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
  if (!iframeDoc) {
    console.error("Could not access iframe document")
    document.body.removeChild(iframe)
    return
  }
  
  iframeDoc.open()
  iframeDoc.write(html)
  iframeDoc.close()
  
  // Wait for content to render, then print
  setTimeout(() => {
    iframe.contentWindow?.focus()
    iframe.contentWindow?.print()
    
    // Clean up after print dialog closes
    setTimeout(() => {
      document.body.removeChild(iframe)
    }, 1000)
  }, 250)
}
